import { CatalogAnalyticsService } from '@analytics/feature';
import { inject, Injectable } from '@angular/core';
import { CustomProduct } from '@product/const';
import { AuthService, ProductService, RoutingService } from '@spartacus/core';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { WishlistService } from './wishlist.service';

@Injectable({
  providedIn: 'root',
})
export class ProductWishlistService {
  private readonly authService = inject(AuthService);
  private readonly routingService = inject(RoutingService);
  private readonly wishlistService = inject(WishlistService);
  private readonly productService = inject(ProductService);
  private readonly catalogAnalyticsService = inject(CatalogAnalyticsService);

  private productAdded$ = new Subject<string | null>();

  getProductAdded(): Observable<string | null> {
    return this.productAdded$.asObservable();
  }

  setProductAdded(productCode: string): void {
    this.productAdded$.next(productCode);
  }

  hasEntry(productCode: string): Observable<boolean> {
    return this.authService
      .isUserLoggedIn()
      .pipe(
        switchMap((isUserLoggedIn) => (isUserLoggedIn ? this.wishlistService.hasEntry({ productCode }) : of(false))),
      );
  }

  getEntryLoading(productCode: string): Observable<boolean> {
    return this.authService
      .isUserLoggedIn()
      .pipe(
        switchMap((isUserLoggedIn) =>
          isUserLoggedIn ? this.wishlistService.getEntryLoading({ productCode }) : of(false),
        ),
      );
  }

  addOrRemoveEntry(productCode: string, listName: string, hideVisuallyAddition = false): void {
    combineLatest({
      isUserLoggedIn: this.authService.isUserLoggedIn(),
      hasEntry: this.hasEntry(productCode),
      product: this.productService.get(productCode).pipe(filter<CustomProduct>(Boolean)),
    })
      .pipe(take(1))
      .subscribe(({ isUserLoggedIn, hasEntry, product }) => {
        if (!isUserLoggedIn) {
          this.routingService.go({ cxRoute: 'login' });

          return;
        }

        if (hasEntry) {
          this.removeFromWishlist(productCode);
        } else {
          this.addToWishlist(productCode, product, listName, hideVisuallyAddition);
        }
      });
  }

  private addToWishlist(
    productCode: string,
    product: CustomProduct,
    listName: string,
    hideVisuallyAddition: boolean,
  ): void {
    this.wishlistService.addEntry({ productCode });
    this.catalogAnalyticsService.onAddToWishList(product, listName);

    if (!hideVisuallyAddition) {
      this.setProductAdded(productCode);
    }
  }

  private removeFromWishlist(productCode: string): void {
    this.wishlistService.removeEntry({ productCode });
  }
}
